import { useEffect } from 'react'

import { useNavigate, useParams } from 'react-router-dom'

import { ButtonComponent } from '../../components/Button'
import { CreateDealFormComponent } from '../../components/CreateDealForm'
import { LayoutPage } from '../../components/LayoutPage'
import { PageTitleComponent } from '../../components/PageTitleComponent'
import { SpinnerComponent } from '../../components/Spinner'

import { useAppContext } from '../../hooks/useAppContext'
import { useControlSearch } from '../../hooks/useControlSearch'
import { useSimpleState } from '../../hooks/useSimpleState'
import { useView } from '../../hooks/useView'

import './index.css'

const getDealStatus = (deal) => {
    if (!deal)
        return null

    if (deal.deleteDate) {
        return { theme: 'gray', label: 'Deleted' }
    } else if (deal.deadDate) {
        return { theme: 'red', label: 'Dead' }
    } else if (deal.clicksDead > 0) {
        return { theme: 'amber', label: 'Reported Dead' }
    }

    return null
}

const EditDealPage = () => {
    const { id } = useParams()

    const navigate = useNavigate()

    const { _state } = useSimpleState({
        fetching: false,
        deal: null
    })

    const {_state: _errors} = useSimpleState({})

    const {_view: _viewMain} = useView({
        initialView: 'loading',
        views: {
            loading: renderLoading,
            main: renderMain
        }
    })

    const {_control, _results, _params} = useControlSearch({
        beforeFetch: () => {},
        afterFetch: () => {}
    })

    const fetch = () => {
        _control.fetchGetDeal(id).then((res) => {
            if (res.status === 200) {
                _state.set('deal', res.body)
            } else {
                navigate('/')   
            }
        }).catch((res) => {
            navigate('/')
        }).finally(() => {
            _viewMain.set('main')
        })
    }

    useEffect(() => {
        fetch()
    }, [])

    const handleSubmit = (body) => {
        _state.set('fetching', true)

        _control.fetchEdit(body).then(res => {
            if (res.status === 200) {
                navigate('/')
            } else {
                _errors.set(res.errors.errors)
            }
        }).catch(res => {

        }).finally(() => {
            _state.set('fetching', false)
        })
    }

    // status-changing actions refresh the loaded deal in place rather than
    // navigating back to the list — the whole point of having these here
    // is so managing a deal doesn't require leaving this page
    const handleControlAction = (promise) => {
        _state.set('fetching', true)

        promise.then(() => {
            fetch()
        }).finally(() => {
            _state.set('fetching', false)
        })
    }

    function renderLoading() {
        return (
            <div>
                <SpinnerComponent />
            </div>
        )
    }

    // grouped by axis (publish state / dead state / archive state /
    // destructive), not just a flat row of buttons — each group gets its
    // own divider in the CSS so the bar reads as organized controls
    // rather than a pile of actions
    function renderControls() {
        const deal = _state.get('deal')

        if (deal.deleteDate) {
            return (
                <div className="editDeal-group">
                    <ButtonComponent config={{ palette: 'gray' }} events={{
                        handleClick: () => handleControlAction(_control.fetchDelete(deal, false))
                    }}>
                        Undelete
                    </ButtonComponent>
                </div>
            )
        }

        return (
            <>
                <div className="editDeal-group">
                    {deal.publishStartDate && !deal.publishEndDate ? (
                        <ButtonComponent config={{ palette: 'blue' }} events={{
                            handleClick: () => handleControlAction(_control.fetchPublish(deal, false))
                        }}>
                            Unpublish
                        </ButtonComponent>
                    ) : (
                        <ButtonComponent config={{ palette: 'blue' }} events={{
                            handleClick: () => handleControlAction(_control.fetchPublish(deal, true))
                        }}>
                            Publish
                        </ButtonComponent>
                    )}
                </div>

                <div className="editDeal-group">
                    {deal.clicksDead > 0 && !deal.deadDate ? (
                        <ButtonComponent config={{ palette: 'amber' }} events={{
                            handleClick: () => handleControlAction(_control.fetchResetClicksDead(deal))
                        }}>
                            Unmark Reported
                        </ButtonComponent>
                    ) : null}

                    {deal.deadDate ? (
                        <ButtonComponent config={{ palette: 'red' }} events={{
                            handleClick: () => handleControlAction(_control.fetchDead(deal, false))
                        }}>
                            Unmark Dead
                        </ButtonComponent>
                    ) : (
                        <ButtonComponent config={{ palette: 'red' }} events={{
                            handleClick: () => handleControlAction(_control.fetchDead(deal, true))
                        }}>
                            Mark Dead
                        </ButtonComponent>
                    )}
                </div>

                <div className="editDeal-group">
                    {deal.archiveDate ? (
                        <ButtonComponent config={{ palette: 'gray' }} events={{
                            handleClick: () => handleControlAction(_control.fetchArchive(deal, false))
                        }}>
                            Unarchive
                        </ButtonComponent>
                    ) : (
                        <ButtonComponent config={{ palette: 'gray' }} events={{
                            handleClick: () => handleControlAction(_control.fetchArchive(deal, true))
                        }}>
                            Archive
                        </ButtonComponent>
                    )}
                </div>

                <div className="editDeal-group">
                    <ButtonComponent config={{ palette: 'gray' }} events={{
                        handleClick: () => handleControlAction(_control.fetchDelete(deal, true))
                    }}>
                        Delete
                    </ButtonComponent>
                </div>
            </>
        )
    }

    function renderMain() {
        const deal = _state.get('deal')
        const status = getDealStatus(deal)

        return (
            <div>
                <LayoutPage config={{ id: 'ManageDeals' }}>
                    <PageTitleComponent value="Edit Deal" />

                    <div className={`editDeal-controls ${_state.get('fetching') ? 'disabled' : ''}`}>
                        <div className="editDeal-controlsLeft">
                            {renderControls()}
                        </div>

                        <div className="editDeal-controlsRight">
                            <div className="editDeal-group">
                                <span className="editDeal-idChip">#{id}</span>
                                {status ? <span className={`tag tag-${status.theme}`}>{status.label}</span> : null}
                            </div>

                            <div className="editDeal-group">
                                <div className="list-field">
                                    <span className="list-fieldLabel">Clicks</span>
                                    <span className="list-fieldValue">{deal.clicks}</span>
                                </div>

                                <div className="list-field">
                                    <span className="list-fieldLabel">Dead</span>
                                    <span className="list-fieldValue">{deal.clicksDead}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={_state.get('fetching') ? 'disabled' : ''}>
                        <CreateDealFormComponent value={_state.get('deal')} config={{
                            errors: _errors.get(),
                            actionType: 'edit'
                        }} events={{
                            handleSubmit: handleSubmit
                        }} />
                    </div>
                </LayoutPage>
            </div>
        )
    }

    return _viewMain.get()
}

export { EditDealPage }